/**
 * VideoControls renders small camera/mic toggle buttons overlaid on a mate's
 * own video tile (self-view only - see MateBox).
 *
 * Why: camera/mic default on when joining a video-enabled room (see
 * apps/web/src/deps/video/AGENTS.md); mates need a visible, obvious way to
 * turn either off again without leaving the room.
 */
import { IconButton } from "../../../../library/IconButton/IconButton"
import { gamelessTestIds } from "../../../../gameless/GamelessGrid/gameless.testIds"

export interface VideoControlsProps {
  cameraOn?: boolean
  micOn?: boolean
  onToggleCamera?: () => void
  onToggleMic?: () => void
  onToggleSelfViewMinimized?: () => void
  isSelfViewMinimized?: boolean
}

function CameraIcon({ off }: { off: boolean }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 8l6-4v16l-6-4" />
      <rect x="1" y="6" width="14" height="12" rx="2" />
      {off && <line x1="2" y1="2" x2="22" y2="22" />}
    </svg>
  )
}

function MicIcon({ off }: { off: boolean }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      {off && <line x1="2" y1="2" x2="22" y2="22" />}
    </svg>
  )
}

function DownArrowIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 14l-7 7m0 0l-7-7m7 7V3" />
    </svg>
  )
}

function UpArrowIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 10l7-7m0 0l7 7m-7-7v18" />
    </svg>
  )
}

export function VideoControls({
  cameraOn = true,
  micOn = true,
  onToggleCamera,
  onToggleMic,
  onToggleSelfViewMinimized,
  isSelfViewMinimized = false,
}: VideoControlsProps) {
  const buttonClass = "flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"

  return (
    <div className="flex gap-1">
      {onToggleMic && (
        <IconButton
          aria-label={micOn ? "Mute microphone" : "Unmute microphone"}
          title={micOn ? "Mute microphone" : "Unmute microphone"}
          aria-pressed={!micOn}
          onClick={onToggleMic}
          className={buttonClass}
          icon={<MicIcon off={!micOn} />}
        />
      )}
      {onToggleCamera && (
        <IconButton
          aria-label={cameraOn ? "Turn camera off" : "Turn camera on"}
          title={cameraOn ? "Turn camera off" : "Turn camera on"}
          aria-pressed={!cameraOn}
          onClick={onToggleCamera}
          className={buttonClass}
          icon={<CameraIcon off={!cameraOn} />}
        />
      )}
      {onToggleSelfViewMinimized && (
        <IconButton
          data-testid={isSelfViewMinimized ? gamelessTestIds.restoreSelfViewButton : gamelessTestIds.minimizeSelfViewButton}
          aria-label={isSelfViewMinimized ? "Restore self view" : "Minimize self view"}
          title={isSelfViewMinimized ? "Restore self view" : "Minimize self view"}
          onClick={onToggleSelfViewMinimized}
          className={buttonClass}
          icon={isSelfViewMinimized ? <UpArrowIcon /> : <DownArrowIcon />}
        />
      )}
    </div>
  )
}

